import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReviewsSection = ({ reviews, averageRating, totalReviews }) => {
  const [showAllReviews, setShowAllReviews] = useState(false);
  const displayedReviews = showAllReviews ? reviews : reviews?.slice(0, 3);

  const ratingDistribution = [
    { stars: 5, count: 45, percentage: 75 },
    { stars: 4, count: 10, percentage: 17 },
    { stars: 3, count: 3, percentage: 5 },
    { stars: 2, count: 1, percentage: 2 },
    { stars: 1, count: 1, percentage: 1 }
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-4 md:p-6 lg:p-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground mb-4 md:mb-0">
          Reviews & Ratings
        </h2>
        <div className="flex items-center space-x-2">
          <Icon name="Star" size={24} color="var(--color-accent)" strokeWidth={2} fill="var(--color-accent)" />
          <span className="font-heading text-2xl md:text-3xl font-bold text-foreground">
            {averageRating}
          </span>
          <span className="font-body text-base text-muted-foreground">
            ({totalReviews} reviews)
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        <div className="lg:col-span-1 space-y-3">
          {ratingDistribution?.map((rating) => (
            <div key={rating?.stars} className="flex items-center space-x-3">
              <span className="font-body text-sm font-medium text-foreground w-12">
                {rating?.stars} star
              </span>
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-accent rounded-full transition-smooth"
                  style={{ width: `${rating?.percentage}%` }}
                />
              </div>
              <span className="font-body text-sm text-muted-foreground w-12 text-right">
                {rating?.count}
              </span>
            </div>
          ))}
        </div>

        <div className="lg:col-span-2 space-y-6">
          {displayedReviews?.map((review) => (
            <div key={review?.id} className="pb-6 border-b border-border last:border-0">
              <div className="flex items-start space-x-4 mb-4">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                  <Image
                    src={review?.userAvatar}
                    alt={review?.userAvatarAlt}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                    <h4 className="font-body text-base md:text-lg font-semibold text-foreground">
                      {review?.userName}
                    </h4>
                    <span className="font-caption text-sm text-muted-foreground">
                      {formatDate(review?.date)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1 mb-3">
                    {[...Array(5)]?.map((_, index) => (
                      <Icon
                        key={index}
                        name="Star"
                        size={16}
                        color={index < review?.rating ? 'var(--color-accent)' : 'var(--color-muted)'}
                        strokeWidth={2}
                        fill={index < review?.rating ? 'var(--color-accent)' : 'none'}
                      />
                    ))}
                  </div>
                  <p className="font-body text-sm md:text-base text-foreground leading-relaxed">
                    {review?.comment}
                  </p>
                  {review?.response && (
                    <div className="mt-4 pl-4 border-l-2 border-primary">
                      <p className="font-body text-sm font-medium text-foreground mb-1">
                        Response from owner:
                      </p>
                      <p className="font-body text-sm text-muted-foreground">
                        {review?.response}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {reviews?.length > 3 && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="lg"
            iconName={showAllReviews ? 'ChevronUp' : 'ChevronDown'}
            iconPosition="right"
            onClick={() => setShowAllReviews(!showAllReviews)}
          >
            {showAllReviews ? 'Show Less Reviews' : `Show All ${reviews?.length} Reviews`}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ReviewsSection;